import { Alert, Box, constants, Stack, Text } from '@redbubble/design-system';
import { defineMessages, IntlShape, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { AnalyticsPromotionCreativeSlot } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import Image from 'next/legacy/image';
import { InView } from 'react-intersection-observer';
import { trackGA4ViewPromotion } from '../../pages/cart/utils/analytics';
import success from '../../public/icons/success.svg';
import styles from './Messages.module.scss';
import {
  Message as MessageInterface,
  MessageSeverity,
  MessageType,
} from './types';
import { unavailableItemMessages } from './unavailableItemMessages';
export const intlMessages = defineMessages({
  listLabel: {
    defaultMessage: 'Cart messages',
  },
});

export interface MessagesProp {
  messages: MessageInterface[];
  hideAppliedIcons?: boolean;
}

export const getMsgText = (
  text: string,
  type: MessageType | undefined,
  intl: IntlShape
): string => {
  if (type === MessageType.NonsaleableProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.nonSaleableItem);
  }

  if (type === MessageType.ExpiredProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.expiredItem);
  }

  return text;
};

export const getActionUrl = (
  url: string,
  type: MessageType | undefined,
  locale: string
) => {
  if (type === MessageType.NonsaleableProductRemoval) {
    // TODO[BX-767]: work out how and where we want to normalise urls
    return url.replace(
      '/articles/',
      `/${locale === 'en' ? 'en-us' : locale}/articles/`
    );
  }

  return url;
};

export const Message = ({
  text,
  severity,
  actionUrl,
  showAppliedIcon,
  onScrollInView,
  onClick,
}: {
  text: string;
  severity: MessageSeverity;
  actionUrl?: string;
  showAppliedIcon?: boolean;
  onScrollInView?: () => void;
  onClick?: () => void;
}) => {
  const content = (
    <Stack direction={constants.HORIZONTAL} spacing="m">
      {showAppliedIcon && (
        <Image
          alt=""
          layout="fixed"
          src={success}
          width={22}
          height={24}
          aria-hidden
        />
      )}
      <Text>{text}</Text>
    </Stack>
  );

  return (
    <InView onChange={onScrollInView} as="li" triggerOnce>
      <Alert intent={severity.toLocaleLowerCase()} label={false}>
        {actionUrl ? (
          <a href={actionUrl} className={styles.link} onClick={onClick}>
            {content}
          </a>
        ) : (
          content
        )}
      </Alert>
    </InView>
  );
};

export const Messages = ({ messages, hideAppliedIcons }: MessagesProp) => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const intl = useIntl();

  if (messages.length === 0) {
    return <div />;
  }

  const handleInViewTracking = (message: MessageInterface) => {
    if (message.text && message.type === MessageType.DiscountSitewide) {
      trackGA4ViewPromotion({
        promotionName: message.text,
        creativeSlot: AnalyticsPromotionCreativeSlot.CartMessages,
      });
    }
  };

  return (
    // NOTE: it would've been better to use a <Stack /> here but I couldn't pass
    // in a classname to get rid of the default <ul> styling
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(intlMessages.listLabel)}
    >
      {messages.map((message) => (
        <Message
          key={`${message.text}_${message.type}_${message.itemId}`}
          actionUrl={
            message.actionUrl
              ? getActionUrl(message.actionUrl, message.type, locale)
              : undefined
          }
          severity={message.severity}
          text={getMsgText(message.text || '', message.type, intl)}
          showAppliedIcon={message.applied && !hideAppliedIcons}
          onScrollInView={() => handleInViewTracking(message)}
        />
      ))}
    </Box>
  );
};
